import { CommonFormGrid } from 'components/form/grid';
import { FormHeading } from 'components/form/heading';
import { FormSelect } from 'components/form/select';
import { FormTextArea } from 'components/form/textarea';
import { FormTextField } from 'components/form/textfield';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { OptionDef } from 'interfaces/options';
import { Currency } from 'lib/enums/currency';
import { SecurityClass, SecurityType } from 'lib/enums/security';
import { SecurityDoc, SecurityWhole } from 'lib/interfaces/database/security';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useIssuersStore from 'stores/issuers';
import useSecuritiesStore from 'stores/securities';

export const SecurityForm = () => {
  const { classOptions, typeOptions } = useSecuritiesStore();
  const { data: issuers, load: loadIssuers } = useIssuersStore();

  useEffect(() => {
    loadIssuers();
  }, []);

  const { data, setData } = useContext(FormContext);

  const [defaultData] = useState<Partial<SecurityDoc>>(data);

  const issuerOptions = useMemo<OptionDef[]>(
    () => issuers.map((m) => ({ label: m.name, value: m.id })),
    [issuers]
  );

  return (
    <EB>
      <CommonFormGrid columns="2">
        <FormHeading label="Security" gridColumn="span 2" />

        <FormSelect
          label="Issuer"
          defaultValue={defaultData.issuerID}
          options={issuerOptions}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              issuerID: v,
            };
            setData(payload);
          }}
          required
        />

        <FormTextField
          label="Name"
          defaultValue={defaultData.name}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              name: v,
            };
            setData(payload);
          }}
          required
        />

        <FormTextField
          label="Code"
          defaultValue={defaultData.code}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              code: v,
            };
            setData(payload);
          }}
          required
        />

        <FormTextField
          label="FundSERV"
          defaultValue={defaultData.fundServ}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              fundServ: v,
            };
            setData(payload);
          }}
        />

        <FormTextField
          label="Yield Stated %"
          type="number"
          defaultValue={StringUtil.printPercent(defaultData.yieldStated)}
          onNumericChange={(v) => {
            const payload: Partial<SecurityWhole> = {
              yieldStated: v / 100,
              yieldStatedPercent: v,
            };
            setData(payload);
          }}
        />

        <FormTextField
          label="Yield Final %"
          type="number"
          defaultValue={StringUtil.printPercent(defaultData.yieldFinal)}
          onNumericChange={(v) => {
            const payload: Partial<SecurityWhole> = {
              yieldFinal: v / 100,
              yieldFinalPercent: v,
            };
            setData(payload);
          }}
        />

        <FormSelect
          label="Currency"
          defaultValue={defaultData.currency}
          options={Object.values(Currency).map((c) => ({
            label: c,
            value: c,
          }))}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              currency: v as Currency,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          gridColumnStart="1"
          label="Class"
          defaultValue={defaultData.class}
          options={classOptions}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              class: v as SecurityClass,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Type"
          disabled={!data.class}
          defaultValue={defaultData.type}
          options={typeOptions.filter((o) => o.group === data.class)}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              type: v as SecurityType,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Dividend"
          defaultValue={defaultData.hasDividend?.toString()}
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
          onBooleanChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              hasDividend: v,
            };
            setData(payload);
          }}
          required
        />

        <FormTextArea
          gridColumn="span 2"
          label="Notes"
          defaultValue={defaultData.notes}
          onChange={(v) => {
            const payload: Partial<SecurityDoc> = {
              notes: v,
            };
            setData(payload);
          }}
        />
      </CommonFormGrid>
    </EB>
  );
};
